// ONE decision block on the Work card (brief §5.3.2): a choice set rendered as a selectable group
// (recommended pre-selected, ordered recommended-first — selecting a sibling re-points the block's
// tick) or a standalone proposal. Verdict controls are STAGING TICKS only — approve, or reject with
// a mandatory reason — freely changeable, committing NOTHING (the ONE commit is the Execute bar,
// brief §3.5). Controls render only where `canRule` is true (rendering only — the owning module is
// the boundary, G-180); every displayed state derives from the projection round (G-182); already-
// ruled options collapse to quiet done rows. Tokens only (G-181); machine state rides data attrs.

import { useEffect, useRef, useState } from 'react';
import type { DecisionBlockView, IntentionView } from '../projections.ts';
import { Dot } from '../../primitives.tsx';
import { HashChip, WorkMiniBadge, type WorkBadgeKind } from '../work-ui.tsx';
import { PayloadView } from './PayloadView.tsx';
import { openOptionsOf, type StageTick } from './staging.ts';

const GHOST_BTN =
  'rounded-sm border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:border-border-strong disabled:opacity-60';
const STAGED_GO_BTN =
  'rounded-sm border border-sage bg-forest-tint px-3 py-1.5 text-sm font-medium text-forest disabled:opacity-60';
const STAGED_STOP_BTN =
  'rounded-sm border border-bracken bg-bracken-tint px-3 py-1.5 text-sm font-medium text-bracken disabled:opacity-60';

/** The §3.3 repeat marker — a worded chip on exactly the re-proposed option, never colour alone. */
function RepeatMarker({ option }: { readonly option: IntentionView }): React.JSX.Element | null {
  if (!option.flaggedRepeat) return null;
  return (
    <span
      data-flagged-repeat={option.id}
      className="ritona-numeric rounded-sm border border-bracken bg-bracken-tint px-1.5 py-0.5 text-xs text-bracken"
    >
      proposed again after a reject
    </span>
  );
}

export interface DecisionBlockProps {
  readonly block: DecisionBlockView;
  readonly index: number;
  /** The selected sibling's id (panel-resolved; the recommended option by default). */
  readonly selectedId: string | null;
  readonly tick: StageTick | null;
  /** The linked proposal's title when this block `dependsOn` one; null when free. */
  readonly dependencyTitle: string | null;
  readonly canRule: boolean;
  readonly busy: boolean;
  readonly onSelect: (optionId: string) => void;
  /** Stage or clear this block's tick (null clears — staging is free, brief §3.5). */
  readonly onStage: (tick: StageTick | null) => void;
}

/** The block head: order number, selected title, action route, dependency chip, staged badge. */
function BlockHead({
  index,
  option,
  tick,
  dependencyTitle,
}: {
  readonly index: number;
  readonly option: IntentionView;
  readonly tick: StageTick | null;
  readonly dependencyTitle: string | null;
}): React.JSX.Element {
  const badge: { readonly kind: WorkBadgeKind; readonly text: string } =
    tick === null
      ? { kind: 'decide', text: 'gated' }
      : tick.verdict === 'approve'
        ? { kind: 'go', text: 'staged · approve' }
        : { kind: 'stop', text: 'staged · reject' };
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
      <span className="ritona-numeric text-xs text-text-subtle">{String(index + 1)}</span>
      <span className="text-sm font-medium text-text">{option.title}</span>
      <span className="ritona-numeric text-xs text-text-subtle">
        {option.actionType} → {option.owningModule}
      </span>
      <RepeatMarker option={option} />
      {dependencyTitle !== null ? (
        <span
          data-depends-on={dependencyTitle}
          className="ritona-numeric rounded-sm border border-border bg-surface-sunken px-1.5 py-0.5 text-xs text-text-muted"
        >
          depends on: {dependencyTitle}
        </span>
      ) : null}
      <span className="ml-auto">
        <WorkMiniBadge kind={badge.kind}>{badge.text}</WorkMiniBadge>
      </span>
    </div>
  );
}

/** One selectable sibling in a choice set (radio semantics; the pick drives payload + staging). */
function ChoiceOption({
  option,
  checked,
  onSelect,
}: {
  readonly option: IntentionView;
  readonly checked: boolean;
  readonly onSelect: (optionId: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      data-option={option.id}
      onClick={() => onSelect(option.id)}
      className={`flex w-full items-start gap-3 px-4 py-3 text-left ${checked ? 'bg-forest-tint' : 'hover:bg-surface-sunken'}`}
    >
      <span className="mt-1">
        <Dot tone={checked ? 'go' : 'neutral'} />
      </span>
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-text">
          {option.title}
          {option.recommended ? (
            <span className="ritona-numeric rounded-sm bg-forest-tint px-1.5 py-0.5 text-xs uppercase text-forest">
              Recommended
            </span>
          ) : null}
          <RepeatMarker option={option} />
        </span>
        {option.summary !== null ? (
          <span className="mt-0.5 block text-sm text-text-muted">{option.summary}</span>
        ) : null}
      </span>
    </button>
  );
}

/** The choice-set selector: pick one sibling; the payload below always shows the pick. */
function ChoiceGroup({
  options,
  selectedId,
  onSelect,
}: {
  readonly options: readonly IntentionView[];
  readonly selectedId: string | null;
  readonly onSelect: (optionId: string) => void;
}): React.JSX.Element {
  return (
    <div role="radiogroup" aria-label="Choice set — pick one" className="divide-y divide-border">
      <div className="ritona-numeric px-4 pt-2.5 text-xs uppercase tracking-eyebrow text-bracken">
        Choice set — pick one
      </div>
      {options.map((option) => (
        <ChoiceOption
          key={option.id}
          option={option}
          checked={option.id === selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

/** The reject editor: the mandatory reason, then stage (still commits nothing — brief §3.5). */
function RejectEditor({
  title,
  busy,
  onConfirm,
  onCancel,
}: {
  readonly title: string;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  // Opening the editor removes the focused "Stage reject" tick — land the keyboard user in the
  // reason box instead of dropping them onto <body>.
  useEffect(() => {
    reasonRef.current?.focus();
  }, []);
  return (
    <div className="border-t border-border bg-surface-sunken px-4 py-3">
      <textarea
        ref={reasonRef}
        rows={2}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        aria-label={`Reason for rejecting: ${title}`}
        placeholder="Why are you rejecting this? The reason trains the next sweep."
        className="w-full rounded-sm border border-border-strong bg-surface px-3 py-2 text-sm text-text"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || draft.trim() === ''}
          onClick={() => onConfirm(draft.trim())}
          aria-label={`Confirm reject: ${title}`}
          className={GHOST_BTN}
        >
          Stage reject
        </button>
        <button type="button" onClick={onCancel} className={GHOST_BTN}>
          Cancel
        </button>
        <span className="ritona-numeric ml-auto text-xs text-text-subtle">
          a reason is required — it is recorded in the ledger
        </span>
      </div>
    </div>
  );
}

/** The two tick buttons + the staged-reject reason echo (freely changeable — brief §3.5 step 1). */
function TickRow({
  option,
  tick,
  busy,
  onStage,
  onOpenReject,
}: {
  readonly option: IntentionView;
  readonly tick: StageTick | null;
  readonly busy: boolean;
  readonly onStage: (tick: StageTick | null) => void;
  readonly onOpenReject: () => void;
}): React.JSX.Element {
  const stagedApprove = tick !== null && tick.verdict === 'approve';
  const stagedReject = tick !== null && tick.verdict === 'reject';
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
      <button
        type="button"
        disabled={busy}
        aria-pressed={stagedApprove}
        aria-label={`Stage approve: ${option.title}`}
        onClick={() => onStage(stagedApprove ? null : { verdict: 'approve' })}
        className={stagedApprove ? STAGED_GO_BTN : GHOST_BTN}
      >
        {stagedApprove ? 'Approve staged' : 'Approve'}
      </button>
      <button
        type="button"
        disabled={busy}
        aria-pressed={stagedReject}
        aria-label={`Stage reject: ${option.title}`}
        onClick={() => (stagedReject ? onStage(null) : onOpenReject())}
        className={stagedReject ? STAGED_STOP_BTN : GHOST_BTN}
      >
        {stagedReject ? 'Reject staged' : 'Reject…'}
      </button>
      {tick !== null && tick.verdict === 'reject' ? (
        <span className="ritona-numeric min-w-0 truncate text-xs text-text-muted">
          reason: {tick.reason}
        </span>
      ) : null}
      <span className="ritona-numeric ml-auto text-xs text-text-subtle">
        ticks commit nothing until Execute
      </span>
    </div>
  );
}

/** The staging footer for one block: the tick row, or the open reject-reason editor. */
function StagingControls({
  option,
  tick,
  busy,
  onStage,
}: {
  readonly option: IntentionView;
  readonly tick: StageTick | null;
  readonly busy: boolean;
  readonly onStage: (tick: StageTick | null) => void;
}): React.JSX.Element {
  const [rejecting, setRejecting] = useState(false);
  if (rejecting) {
    return (
      <RejectEditor
        title={option.title}
        busy={busy}
        onConfirm={(reason) => {
          onStage({ verdict: 'reject', reason });
          setRejecting(false);
        }}
        onCancel={() => setRejecting(false)}
      />
    );
  }
  return (
    <TickRow
      option={option}
      tick={tick}
      busy={busy}
      onStage={onStage}
      onOpenReject={() => setRejecting(true)}
    />
  );
}

/** A block whose every option is already ruled — collapsed to quiet done rows (brief §5.3.2). */
function CollapsedBlock({ block }: { readonly block: DecisionBlockView }): React.JSX.Element {
  return (
    <div className="rounded-md border border-border bg-surface" data-block-status="closed">
      {block.options.map((option) => (
        <div
          key={option.id}
          data-option-status={option.status}
          className="flex items-center gap-3 px-4 py-3 text-sm text-text-muted"
        >
          <span className={option.status === 'superseded' ? 'line-through' : ''}>
            {option.title}
          </span>
          <span className="ritona-numeric ml-auto text-xs uppercase text-text-subtle">
            {option.status}
          </span>
        </div>
      ))}
    </div>
  );
}

/** One decision block: choice-set group or standalone proposal, payload of the pick, staging. */
export function DecisionBlock({
  block,
  index,
  selectedId,
  tick,
  dependencyTitle,
  canRule,
  busy,
  onSelect,
  onStage,
}: DecisionBlockProps): React.JSX.Element {
  const open = openOptionsOf(block);
  const selected = open.find((option) => option.id === selectedId) ?? open[0];
  if (selected === undefined) return <CollapsedBlock block={block} />;
  return (
    <div
      data-block={block.choiceSet ?? selected.id}
      data-staged={tick?.verdict ?? 'none'}
      className="rounded-md border border-border bg-surface"
    >
      <BlockHead index={index} option={selected} tick={tick} dependencyTitle={dependencyTitle} />
      {open.length > 1 ? (
        <ChoiceGroup options={open} selectedId={selected.id} onSelect={onSelect} />
      ) : null}
      <div className="border-t border-border px-4 py-3">
        <div className="ritona-numeric mb-2 flex items-center gap-2 text-xs uppercase tracking-eyebrow text-text-subtle">
          Frozen payload
          <HashChip hash={selected.contentHash} />
        </div>
        <PayloadView option={selected} />
      </div>
      {canRule ? (
        <StagingControls option={selected} tick={tick} busy={busy} onStage={onStage} />
      ) : null}
    </div>
  );
}
